import { useEffect, useMemo, useState } from 'react'
import { testSupabaseConnection, createTablesIfNotExist, mapDatabaseStructure } from '../lib/test-connection'
import { testExpectedTables, testTablesViaREST, testConnectivity } from '../lib/database-inspector'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { CheckCircle, XCircle, Database, Loader2 } from 'lucide-react'
import { runAllSupabaseTests, formatTestRunSummary, type TestRunResult } from '../lib/test-runner'

export function SupabaseTest() {
  const [testResult, setTestResult] = useState<any>(null)
  const [structureResult, setStructureResult] = useState<any>(null)
  const [inspectionResult, setInspectionResult] = useState<any>(null)
  const [connectivityResult, setConnectivityResult] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [runningAll, setRunningAll] = useState(false)
  const [lastRun, setLastRun] = useState<TestRunResult | null>(null)
  const [history, setHistory] = useState<TestRunResult[]>([])

  // Load history from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem('Sabhia-test-history')
      if (raw) setHistory(JSON.parse(raw))
    } catch {}
  }, [])

  // Persist history
  useEffect(() => {
    try {
      localStorage.setItem('Sabhia-test-history', JSON.stringify(history.slice(0, 10)))
    } catch {}
  }, [history])

  const overallStatus = useMemo(() => {
    if (!lastRun) return null
    return lastRun.summary.failed === 0
  }, [lastRun])

  const runTest = async () => {
    setIsLoading(true)
    setTestResult(null)
    
    try {
      const result = await testSupabaseConnection()
      setTestResult(result)
    } catch (error) {
      setTestResult({
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const generateTables = async () => {
    const result = await createTablesIfNotExist()
    console.log('Resultado da criação de tabelas:', result)
  }

  const mapStructure = async () => {
    setIsLoading(true)
    setStructureResult(null)

    try {
      const result = await mapDatabaseStructure()
      setStructureResult(result)
    } catch (error) {
      setStructureResult({
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const inspectDB = async () => {
    setIsLoading(true)
    setInspectionResult(null)

    try {
      const result = await testExpectedTables()
      setInspectionResult(result)
    } catch (error) {
      setInspectionResult({
        error: error instanceof Error ? error.message : 'Erro desconhecido'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const testViaREST = async () => {
    setIsLoading(true)
    setInspectionResult(null)

    try {
      const result = await testTablesViaREST()
      setInspectionResult(result)
    } catch (error) {
      setInspectionResult({
        error: error instanceof Error ? error.message : 'Erro desconhecido'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const testConnect = async () => {
    setIsLoading(true)
    setConnectivityResult(null)

    try {
      const result = await testConnectivity()
      setConnectivityResult(result)
    } catch (error) {
      setConnectivityResult([{
        name: 'Error',
        error: error instanceof Error ? error.message : 'Erro desconhecido'
      }])
    } finally {
      setIsLoading(false)
    }
  }

  const runAll = async () => {
    setRunningAll(true)
    try {
      const run = await runAllSupabaseTests()
      setLastRun(run)
      setHistory(prev => [run, ...prev].slice(0, 10))
      // Also update the individual panels when possible
      setTestResult(run.results.testSupabaseConnection)
      setStructureResult(run.results.mapDatabaseStructure)
      setInspectionResult(run.results.testExpectedTables)
      setConnectivityResult(run.results.testConnectivity)
    } catch (e) {
      console.error('Run all tests failed', e)
    } finally {
      setRunningAll(false)
    }
  }

  const exportResults = () => {
    const payload = JSON.stringify({ lastRun, history }, null, 2)
    const blob = new Blob([payload], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `supabase-test-results-${Date.now()}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const copySummary = async () => {
    if (!lastRun) return
    const text = formatTestRunSummary(lastRun)
    await navigator.clipboard.writeText(text)
    alert('Resumo copiado!')
  }

  return (
    <div className="min-h-screen p-8 bg-background">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-2">Teste de Conexão Supabase</h1>
          <p className="text-muted-foreground">
            Verificar conectividade e estrutura do banco de dados
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              Teste de Conectividade
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Overall summary */}
            {lastRun && (
              <div className={`p-3 rounded border ${overallStatus ? 'border-green-300 bg-green-50 dark:bg-green-900/20' : 'border-red-300 bg-red-50 dark:bg-red-900/20'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {overallStatus ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                    <span className="font-medium">Resumo Geral:</span>
                    <span className="text-sm text-muted-foreground">
                      {lastRun.summary.passed} passed, {lastRun.summary.failed} failed, {lastRun.summary.total} total
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={copySummary}>Copiar Resumo</Button>
                    <Button variant="outline" onClick={exportResults}>Exportar JSON</Button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-4 flex-wrap">
              <Button onClick={runAll} disabled={runningAll || isLoading} className="bg-black text-white hover:bg-black/80 dark:bg-white dark:text-black dark:hover:bg-white/80">
                {runningAll ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Executando todos...
                  </>
                ) : (
                  '▶️ Executar Todos os Testes'
                )}
              </Button>

              <Button onClick={runTest} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Testando...
                  </>
                ) : (
                  'Executar Teste'
                )}
              </Button>

              <Button variant="outline" onClick={mapStructure} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Mapeando...
                  </>
                ) : (
                  'Mapear Estrutura DB'
                )}
              </Button>

              <Button onClick={inspectDB} disabled={isLoading} className="bg-green-600 hover:bg-green-700">
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Inspecionando...
                  </>
                ) : (
                  '🎯 Inspecionar Tabelas'
                )}
              </Button>

              <Button onClick={testViaREST} disabled={isLoading} className="bg-purple-600 hover:bg-purple-700">
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Testando REST...
                  </>
                ) : (
                  '🌐 Testar via REST API'
                )}
              </Button>

              <Button onClick={testConnect} disabled={isLoading} className="bg-blue-600 hover:bg-blue-700">
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Testando...
                  </>
                ) : (
                  '🔗 Testar Conectividade'
                )}
              </Button>

              <Button variant="outline" onClick={generateTables}>
                Gerar Scripts SQL
              </Button>
            </div>

            {testResult && (
              <div className="mt-6 space-y-4">
                <div className="flex items-center gap-2">
                  {testResult.success ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500" />
                  )}
                  <span className="font-medium">
                    {testResult.success ? 'Conexão bem-sucedida!' : 'Falha na conexão'}
                  </span>
                </div>

                {testResult.error && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <p className="text-red-700 dark:text-red-300">
                      <strong>Erro:</strong> {testResult.error}
                    </p>
                  </div>
                )}

                {testResult.tables && (
                  <div className="space-y-2">
                    <h3 className="font-medium">Status das Tabelas:</h3>
                    {Object.entries(testResult.tables).map(([table, status]: [string, any]) => (
                      <div key={table} className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                        {status.exists ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                        <span className="font-mono">{table}</span>
                        <span className="text-sm text-muted-foreground">
                          {status.exists ? 'OK' : status.error}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {testResult.auth && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <h3 className="font-medium mb-2">Status da Autenticação:</h3>
                    <p>Sessão: {testResult.auth.session}</p>
                    {testResult.auth.user && (
                      <p>Usuário: {testResult.auth.user}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {structureResult && (
              <div className="mt-6 space-y-4">
                <h3 className="font-medium text-lg">📊 Estrutura do Banco de Dados</h3>

                {structureResult.success ? (
                  <div className="space-y-4">
                    {Object.entries(structureResult.structure).map(([table, info]: [string, any]) => (
                      <div key={table} className="p-4 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Database className="w-4 h-4" />
                          <span className="font-medium font-mono">{table}</span>
                          <span className="text-sm text-muted-foreground">
                            ({info.count} registros)
                          </span>
                        </div>

                        <div className="space-y-2">
                          <p className="text-sm font-medium">Colunas:</p>
                          <div className="flex flex-wrap gap-1">
                            {info.columns.map((col: string) => (
                              <span key={col} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded font-mono">
                                {col}
                              </span>
                            ))}
                          </div>

                          {info.sample && (
                            <details className="mt-2">
                              <summary className="text-sm font-medium cursor-pointer">
                                Exemplo de registro
                              </summary>
                              <pre className="mt-2 p-2 bg-background rounded text-xs overflow-auto">
                                {JSON.stringify(info.sample, null, 2)}
                              </pre>
                            </details>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <p className="text-red-700 dark:text-red-300">
                      <strong>Erro:</strong> {structureResult.error}
                    </p>
                  </div>
                )}
              </div>
            )}

            {inspectionResult && (
              <div className="mt-6 space-y-4">
                <h3 className="font-medium text-lg">🎯 Inspeção das Tabelas Esperadas</h3>

                <div className="grid gap-4">
                  {Object.entries(inspectionResult).map(([tableName, info]: [string, any]) => (
                    <div key={tableName} className="p-4 border rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        {info.exists ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                        <span className="font-medium font-mono text-lg">{tableName}</span>
                        {info.exists && (
                          <span className="text-sm text-muted-foreground">
                            ({info.count} registros)
                          </span>
                        )}
                      </div>

                      {info.exists ? (
                        <div className="space-y-3">
                          <div>
                            <p className="text-sm font-medium mb-2">Colunas encontradas:</p>
                            <div className="flex flex-wrap gap-1">
                              {info.columns.map((col: string) => (
                                <span key={col} className={`px-2 py-1 text-xs rounded font-mono ${
                                  col === 'link_pdf' || col === 'status_entrega'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                }`}>
                                  {col}
                                  {(col === 'link_pdf' || col === 'status_entrega') && ' ✨'}
                                </span>
                              ))}
                            </div>
                          </div>

                          {info.sample && (
                            <details className="mt-3">
                              <summary className="text-sm font-medium cursor-pointer hover:text-primary">
                                📋 Ver exemplo de dados
                              </summary>
                              <pre className="mt-2 p-3 bg-muted rounded text-xs overflow-auto max-h-40">
                                {JSON.stringify(info.sample, null, 2)}
                              </pre>
                            </details>
                          )}
                        </div>
                      ) : (
                        <div className="text-red-600 dark:text-red-400">
                          <p className="text-sm">❌ Tabela não encontrada</p>
                          {info.error && (
                            <p className="text-xs mt-1 opacity-75">Erro: {info.error}</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {connectivityResult && (
              <div className="mt-6 space-y-4">
                <h3 className="font-medium text-lg">🔗 Teste de Conectividade</h3>

                <div className="grid gap-3">
                  {connectivityResult.map((test: any, index: number) => (
                    <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
                      {test.ok ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{test.name}</span>
                          <span className={`px-2 py-1 text-xs rounded ${
                            test.ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {test.status}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">{test.description}</p>
                        {test.error && (
                          <p className="text-xs text-red-600 mt-1">{test.error}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* History */}
        {history.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Execuções</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                {history.map((h, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 border rounded">
                    <div>
                      <div className="font-mono">{h.startedAt} → {h.finishedAt}</div>
                      <div className="text-muted-foreground">{h.summary.passed} passed, {h.summary.failed} failed</div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={async () => {
                        await navigator.clipboard.writeText(JSON.stringify(h, null, 2))
                      }}>Copiar JSON</Button>
                      <Button size="sm" variant="outline" onClick={async () => {
                        await navigator.clipboard.writeText(formatTestRunSummary(h))
                      }}>Copiar Resumo</Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Próximos Passos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <p>1. ✅ Configurar cliente Supabase</p>
              <p>2. 🔄 Testar conexão com o banco</p>
              <p>3. 📋 Verificar/criar estrutura das tabelas</p>
              <p>4. 🔐 Implementar autenticação via magic link</p>
              <p>5. 🚀 Migrar para Tanstack Start</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
